import type { T } from '../../../messages';
import type { BlockSpec } from './specs';
import { optionLabel } from './slots';

/**
 * **What the builder calls things**, in the console's language — a section
 * type's name and its one-line purpose, a view's name.
 *
 * The console's own words first (`builder.typeName`, `builder.typePurpose`,
 * `builder.viewName` — tables in `messages/areas/builder.ts`, English and
 * Spanish), the vocabulary's English second. The second is a floor, not a
 * plan: a block a newer server serves is named by its own label until the
 * console learns a word for it, rather than by nothing.
 */

export function sectionName(t: T, type: string, spec: BlockSpec | undefined): string {
  const own = t('builder.typeName', { type });
  return own !== '' ? own : (spec?.label ?? type);
}

export function sectionPurpose(t: T, type: string, spec: BlockSpec | undefined): string {
  const own = t('builder.typePurpose', { type });
  return own !== '' ? own : (spec?.why ?? '');
}

export function viewLabel(t: T, view: string): string {
  const own = t('builder.viewName', { view });
  return own !== '' ? own : optionLabel(t, view);
}

/** The names the canvas labels its bands with (`draft.labels`), for every block. */
export function canvasLabels(t: T, specs: Map<string, BlockSpec>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [type, spec] of specs) {
    // The wire's own limits: a slot-shaped name and eighty characters.
    if (!/^[a-z][a-z0-9_]{0,39}$/.test(type)) continue;
    out[type] = sectionName(t, type, spec).slice(0, 80);
    if (Object.keys(out).length >= 64) break;
  }
  return out;
}
